// Reports API client - Phase 4 Plan 04-06 Task 2 (DIS-19, DIS-21, D-76).
//
// Wraps:
//   POST /api/v1/reports/generate              (sync xlsx blob OR 202 + JSON)
//   GET  /api/v1/reports/{id}/status           (poll until status='ready')
//   GET  /api/v1/reports                       (last 30 days)
//   POST /api/v1/reports/{id}/regenerate       (failed/expired only)

import type { ApiClient } from "./client";

export type ReportType =
  | "trips_period"
  | "per_driver_summary"
  | "per_customer_summary"
  | "problems_log"
  | "monthly_summary";

export type ReportFormat = "xlsx" | "pdf";
export type ReportLocale = "en" | "tr" | "bg";

export type ReportStatus = "pending" | "generating" | "ready" | "failed" | "expired";

export interface ReportFilters {
  from?: string | null;
  to?: string | null;
  driver_id?: number | null;
  customer_email?: string | null;
  status?: string | null;
}

export interface ReportGenerateBody {
  report_type: ReportType;
  format: ReportFormat;
  filters: ReportFilters;
  locale: ReportLocale;
}

export interface ReportResponse {
  id: number;
  status: ReportStatus;
  report_type: ReportType;
  format: ReportFormat;
  locale: ReportLocale;
  created_at: string;
  poll_url: string;
  download_url: string | null;
  expires_at: string | null;
  error_message: string | null;
}

export interface ReportListResponse {
  items: ReportResponse[];
  total: number;
}

/** Result type for the dual sync-vs-async generate endpoint. */
export type GenerateResult =
  | { kind: "sync"; blob: Blob; filename: string }
  | { kind: "async"; report: ReportResponse };

/** Pull a filename out of a Content-Disposition header. */
function parseFilename(headerValue: string | null, fallback: string): string {
  if (!headerValue) return fallback;
  const m = /filename="?([^";]+)"?/i.exec(headerValue);
  return m?.[1] ? m[1] : fallback;
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const reportsApi = {
  /**
   * POST /api/v1/reports/generate
   *
   * The server returns EITHER:
   *   - HTTP 200 with Content-Type=...spreadsheetml.sheet (sync fast-path).
   *   - HTTP 202 with JSON body matching ReportResponse (async ARQ path).
   *
   * This wrapper does the discrimination via Content-Type. Authorization is
   * attached by the ApiClient; we use raw fetch here because the standard
   * `client.fetch` JSON-parses the response and would clobber the blob path.
   */
  async generate(
    baseUrl: string,
    token: string | null,
    body: ReportGenerateBody,
  ): Promise<GenerateResult> {
    const headers = new Headers();
    headers.set("Content-Type", "application/json");
    headers.set("Accept", "*/*");
    headers.set("Idempotency-Key", generateIdempotencyKey());
    if (token) headers.set("Authorization", `Bearer ${token}`);

    const res = await fetch(`${baseUrl}/api/v1/reports/generate`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      // Try to surface a JSON {detail} error body to the caller.
      let detail = res.statusText;
      try {
        const errBody = (await res.json()) as { detail?: string };
        if (errBody?.detail) detail = errBody.detail;
      } catch {
        /* not JSON */
      }
      const err = new Error(`Generate failed: ${detail}`);
      (err as Error & { status?: number }).status = res.status;
      throw err;
    }

    const ct = res.headers.get("Content-Type") || "";
    if (ct.includes("spreadsheetml") || ct.includes("application/pdf")) {
      const blob = await res.blob();
      const filename = parseFilename(
        res.headers.get("Content-Disposition"),
        `report_${body.report_type}.${body.format}`,
      );
      return { kind: "sync", blob, filename };
    }
    const report = (await res.json()) as ReportResponse;
    return { kind: "async", report };
  },

  status(client: ApiClient, reportId: number): Promise<ReportResponse> {
    return client.get<ReportResponse>(`/api/v1/reports/${reportId}/status`);
  },

  list(client: ApiClient, opts: { limit?: number } = {}): Promise<ReportListResponse> {
    const params = new URLSearchParams();
    if (opts.limit !== undefined) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return client.get<ReportListResponse>(`/api/v1/reports${qs ? `?${qs}` : ""}`);
  },

  regenerate(client: ApiClient, reportId: number): Promise<ReportResponse> {
    return client.fetch<ReportResponse>(`/api/v1/reports/${reportId}/regenerate`, {
      method: "POST",
      headers: { "Idempotency-Key": generateIdempotencyKey() },
    });
  },
};

/**
 * Trigger a browser download for a Blob with the given filename.
 * Used by the sync fast-path: server returns .xlsx bytes inline.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick to keep the click handler's reference alive.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
